import { useState } from 'react';
import { FileText, Send, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { listFiles, prepareSigningDauer } from '../../lib/vorgangApi';
import SigningEmailReview from '../SigningEmailReview';
import ConfirmDialog from '../admin/ConfirmDialog';
import { DataRequestModal } from '../DataRequestModal';
import { useAuth } from '../../context/AuthContext';

/** Aktionen-Reiter der rechten Spalte: EIN Haupt-Knopf plus eine zweite Reihe.
 *  Der Haupt-Knopf richtet sich nach dem Stand der Dauer-Dokumente — liegen sie alle
 *  vor, geht es zur Unterschrift; fehlt etwas, wird es beim Kunden angefordert. Beides
 *  sind Kunden-Aktionen (ohne Vorgang/Bericht); die erzeugten Formulare erscheinen in
 *  den Dauer-Dokumenten darüber. */
export function AktionenPanel({
  customerId,
  customerEmail,
  dauerVollstaendig,
  onChanged,
}: {
  customerId: number;
  customerEmail?: string | null;
  dauerVollstaendig: boolean;
  onChanged?: () => void;
}) {
  const { user } = useAuth();

  const [generating, setGenerating] = useState(false);
  const [bafaMsg, setBafaMsg] = useState<string | null>(null);
  const [overwriteOpen, setOverwriteOpen] = useState(false);

  const [sendOpen, setSendOpen] = useState(false);
  const [sendMsg, setSendMsg] = useState<string | null>(null);
  const [anfordernOpen, setAnfordernOpen] = useState(false);

  const doGenerateBafaForms = async () => {
    setGenerating(true); setBafaMsg(null);
    try {
      await api.post(`/api/customers/${customerId}/bafa-forms`);
      setBafaMsg('Dauer-Dokumente wurden erzeugt.');
      onChanged?.(); // Dauer-Dokumente-Grid neu laden → Formulare erscheinen dort
    } catch (e: any) {
      setBafaMsg(e?.message ?? 'Fehler beim Generieren');
    } finally {
      setGenerating(false);
    }
  };

  // Vor dem Generieren prüfen, ob bereits Dauer-Formulare existieren — dann NICHT
  // stillschweigend überschreiben, sondern rückfragen (wie im Editor).
  const onGenerateClick = async () => {
    setBafaMsg(null);
    try {
      const files = await listFiles({ customerId });
      if (files.some(f => /vollmacht|grc|minimis/i.test(f.category || ''))) {
        setOverwriteOpen(true);
        return;
      }
    } catch { /* Existenzprüfung best effort — im Zweifel direkt generieren */ }
    await doGenerateBafaForms();
  };

  const hauptKnopf = 'w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50';
  // Weiß mit Rand statt slate-100: die Spalte selbst steht auf hellem Grau, dort ging
  // ein grauer Knopf unter.
  const zweitKnopf = 'w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium rounded-md bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 shadow-sm dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600 transition-colors disabled:opacity-50';

  return (
    <div className="space-y-2">
      {dauerVollstaendig ? (
        <button
          onClick={() => setSendOpen(true)}
          disabled={!customerEmail}
          title={!customerEmail ? 'Kunde hat keine E-Mail-Adresse' : 'Dauer-Dokumente zur Unterschrift an den Kunden senden'}
          className={`${hauptKnopf} bg-emerald-600 hover:bg-emerald-700 text-white`}
        >
          <Send className="w-3.5 h-3.5" /> Zur Unterschrift senden
        </button>
      ) : (
        <button
          onClick={() => setAnfordernOpen(true)}
          title="Dem Kunden einen Link schicken, über den er Daten und Dokumente selbst hinterlegt"
          className={`${hauptKnopf} bg-emerald-600 hover:bg-emerald-700 text-white`}
        >
          <Send className="w-3.5 h-3.5" /> Beim Kunden anfordern
        </button>
      )}

      <button onClick={onGenerateClick} disabled={generating} className={zweitKnopf}>
        {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
        Dauer-Dokumente generieren
      </button>

      {bafaMsg && <p className="text-xs text-slate-500 dark:text-slate-400">{bafaMsg}</p>}
      {sendMsg && <p className="text-xs text-slate-500 dark:text-slate-400">{sendMsg}</p>}

      <SigningEmailReview
        open={sendOpen}
        dauerCustomerId={customerId}
        email={customerEmail ?? ''}
        defaultCc={user?.email}
        context={{}}
        prepare={(email) => prepareSigningDauer(customerId, email)}
        onCancel={() => setSendOpen(false)}
        onSent={() => {
          setSendOpen(false);
          setSendMsg('Dauer-Dokumente wurden zur Unterschrift an den Kunden gesendet.');
          onChanged?.();
        }}
      />

      {anfordernOpen && (
        <DataRequestModal
          customerId={customerId}
          prefillEmail={customerEmail ?? ''}
          onClose={() => setAnfordernOpen(false)}
          onSuccess={() => onChanged?.()}
        />
      )}

      <ConfirmDialog
        open={overwriteOpen}
        title="Dauer-Dokumente überschreiben"
        message="Für diesen Kunden wurden bereits Dauer-Dokumente erzeugt. Neu generieren und die bisherigen (unsignierten) Entwürfe überschreiben? Bereits unterschriebene Fassungen bleiben erhalten."
        confirmLabel="Überschreiben"
        onConfirm={async () => { setOverwriteOpen(false); await doGenerateBafaForms(); }}
        onCancel={() => setOverwriteOpen(false)}
      />
    </div>
  );
}
